import React, { useState, useEffect } from 'react';
import { useClient, useMutation } from 'jsonapi-react';
import { FiMoreHorizontal } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Dropdown from 'src/app/components/Dropdown';
import Modal from 'src/app/components/Modal';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { TabNavbar } from './TabNavbar';
import { useCheckIncludeProfiles, checkIncludeAppName } from '../../hooks/useAuthorityLevel';
import Error404 from 'src/app/components/Error404';
import toast from 'react-hot-toast';
import usePermission from '@/app/hooks/usePermission';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';

export const Taxonomies = props => {
  const { uri } = props;

  const { t } = useTranslation();
  const client = useClient();
  const [addTaxonomy] = useMutation('taxonomies');
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [currentTaxonomy, setCurrentTaxonomy] = useState();
  const [taxonomies, setTaxonomies] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [newTaxonomyName, setNewTaxonomyName] = useState('');
  const [editTaxonomyName, setEditTaxonomyName] = useState('');
  const [activeColumn, setActiveColumn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingTaxonomy, setAddingTaxonomy] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await client.fetch(`taxonomies`);
      setTaxonomies(data);
      setLoading(false);
      return data;
    } catch (e) {
      console.log(e);
    }
  };

  const selectTaxonomy = (taxonomy, currentList, nextList) => {
    let newTopics = taxonomy['taxonomy-children'] || [];

    setTaxonomies([...taxonomies]);
    if (nextList === 'subTopics') {
      topics.map(t => (t.isActive = false));
      taxonomy.isActive = true;
      setSubTopics([...newTopics]);
    } else if (nextList === 'topics') {
      taxonomies.map(t => (t.isActive = false));
      taxonomy.isActive = true;
      setTopics([...newTopics]);
      setSubTopics([]);
    } else {
      subTopics.map(t => (t.isActive = false));
      taxonomy.isActive = true;
      setSubTopics([...subTopics]);
    }
  };

  const newTaxonomyForm = currentList => {
    setActiveColumn(currentList);
    setShowModal(true);
  };

  const editTaxonomyForm = (currentList, taxonomy) => {
    setCurrentTaxonomy(taxonomy);
    setActiveColumn(currentList);
    setEditTaxonomyName(taxonomy.name);
    setEditModal(true);
  };

  const destroyTaxonomyForm = (currentList, taxonomy) => {
    Swal.fire({
      title: t('taxonomyAndMaterials.warningDeleteTaxonomy'),
      text: t('projects.warningDeleteCannotBeUndoneText'),
      confirmButtonText: t('button.yes'),
      showDenyButton: true,
      denyButtonText: t('button.no'),
      showCloseButton: true
    }).then(result => {
      if (result.isConfirmed) {
        destroyCurrentTaxonomy(currentList, taxonomy);
      }
    });
  };

  const destroyCurrentTaxonomy = async (currentList, taxonomy) => {
    const { error, errors } = await client.delete(['taxonomies', taxonomy.id]);

    if (error && error?.id === 'base') {
      Swal.fire('Ops...', `${error.title}`, 'error');
    } else if (error) {
      Swal.fire('Ops...', t('toast.errorTryAgain'), 'error');
    } else {
      if (currentList === 'topics') {
        let newList = topics;
        newList = newList.filter(item => item.id !== taxonomy.id);
        setTopics([...newList]);
      } else if (currentList === 'subTopics') {
        let newList = subTopics;
        newList = newList.filter(item => item.id !== taxonomy.id);
        setSubTopics([...newList]);
      } else {
        let newList = taxonomies;
        newList = newList.filter(item => item.id !== taxonomy.id);
        setTaxonomies([...newList]);
      }
      Swal.fire('Excluida!', 'A taxonomia foi deletada.', 'success');
    }
  };

  const editCurrentTaxonomy = async e => {
    e.preventDefault();

    const { data, error } = await client.mutate(['taxonomies', currentTaxonomy.id], {
      name: editTaxonomyName
    });

    if (error) {
      toast.error('Erro ao editar taxonomia.');
    } else {
      const newTaxonomyList = updateTaxonomyList(taxonomies, currentTaxonomy.id, data);
      setTaxonomies(newTaxonomyList);

      setEditModal(false);
      setCurrentTaxonomy(null);
      setEditTaxonomyName('');
      toast.success(t('toast.successEditTaxonomy'));
    }
  };

  const updateTaxonomyList = (list, id, newItem) => {
    return list.map(item => {
      if (item.id === id) {
        item.name = newItem.name;
      } else if (item['taxonomy-children']) {
        item['taxonomy-children'] = updateTaxonomyList(item['taxonomy-children'], id, newItem);
      }

      return item;
    });
  };

  const findTaxonomyById = (taxonomies, id) => {
    for (const taxonomy of taxonomies) {
      if (taxonomy.id === id) return taxonomy;
      if (taxonomy['taxonomy-children']) {
        const found = findTaxonomyById(taxonomy['taxonomy-children'], id);
        if (found) return found;
      }
    }
    return null;
  };

  const addNewTaxonomy = async e => {
    e.preventDefault();
    setAddingTaxonomy(true);

    let taxonomyParentId;
    if (activeColumn === 'topics') {
      taxonomyParentId = taxonomies.find(item => item.isActive)?.id;
    } else if (activeColumn === 'subTopics') {
      taxonomyParentId = topics.find(item => item.isActive)?.id;
    }

    addTaxonomy({
      name: newTaxonomyName,
      taxonomyParentId: taxonomyParentId
    }).then(async ({ data: newTaxonomy, error }) => {
      if (error) {
        toast.error(t('toast.errorCreateTaxonomy'));
      } else {
        const updatedTaxonomies = await fetchData();

        const addedTaxonomy = findTaxonomyById(updatedTaxonomies, newTaxonomy.id);

        const findParentHierarchy = (taxonomies, topicId) => {
          for (const taxonomy of taxonomies) {
            if (taxonomy['taxonomy-children']) {
              const topic = taxonomy['taxonomy-children'].find(t => t.id === topicId);
              if (topic) return { parentTaxonomy: taxonomy, parentTopic: topic };
              const nested = findParentHierarchy(taxonomy['taxonomy-children'], topicId);
              if (nested) return nested;
            }
          }
          return null;
        };

        if (addedTaxonomy) {
          if (taxonomyParentId && activeColumn === 'topics') {
            const activeTaxonomy = updatedTaxonomies.find(t => t.id === taxonomyParentId);
            const activatedTaxonomies = updatedTaxonomies.map(taxonomy => ({
              ...taxonomy,
              isActive: taxonomy.id === activeTaxonomy.id
            }));
            setTaxonomies(activatedTaxonomies);
            setTopics(activeTaxonomy['taxonomy-children'] || []);
          } else if (taxonomyParentId && activeColumn === 'subTopics') {
            const hierarchy = findParentHierarchy(updatedTaxonomies, taxonomyParentId);

            if (hierarchy) {
              const activatedTaxonomies = updatedTaxonomies.map(taxonomy => ({
                ...taxonomy,
                isActive: taxonomy.id === hierarchy.parentTaxonomy.id,
                'taxonomy-children': taxonomy['taxonomy-children']?.map(topic => ({
                  ...topic,
                  isActive: topic.id === hierarchy.parentTopic.id
                }))
              }));

              setTaxonomies(activatedTaxonomies);

              setTopics(activatedTaxonomies.find(t => t.isActive)?.['taxonomy-children'] || []);
              setSubTopics(hierarchy.parentTopic['taxonomy-children'] || []);
            }
          } else {
            const activatedTaxonomies = updatedTaxonomies.map(taxonomy => ({
              ...taxonomy,
              isActive: taxonomy.id === addedTaxonomy.id
            }));
            setTaxonomies(activatedTaxonomies);
          }
        }

        setShowModal(false);
        setNewTaxonomyName('');
        toast.success(t('toast.successCreateTaxonomy'));
      }
      setAddingTaxonomy(false);
    });
  };

  const breadCrumbs = {
    title: 'Organização da Plataforma',
    nav: [
      {
        route: uri,
        name: 'Ementa',
        isActive: true
      }
    ]
  };

  return (
    <>
      {usePermission('Visualizar lista de ementas, tópicos e subtópicos', 'Ementas') ? (
        <>
          <div className="main-content main-content--block">
            <Breadcrumbs data={breadCrumbs} />

            {/* <TabNavbar selectedTab="taxonomies" /> */}

            <div className="taxonomies">
              <div className="taxonomies__column">
                {usePermission('Criar ementas', 'Ementas') && (
                  <button
                    className="btn btn--outline btn--full"
                    onClick={() => newTaxonomyForm('taxonomies')}
                  >
                    {t('taxonomyAndMaterials.buttonAddTaxonomy')}
                  </button>
                )}

                {loading && <Loader />}

                <div className="taxonomies__list">
                  {taxonomies &&
                    taxonomies.map((taxonomy, index) => (
                      <div
                        key={index}
                        className={`taxonomies__item ${taxonomy.isActive ? 'active' : ''}`}
                        onClick={() => selectTaxonomy(taxonomy, 'taxonomies', 'topics')}
                      >
                        <span className="taxonomies__text">{taxonomy.name}</span>

                        {usePermission('Editar ementas', 'Ementas') || usePermission('Excluir ementas', 'Ementas') ? (
                          <Dropdown>
                            <Dropdown.Toggle>
                              <FiMoreHorizontal />
                            </Dropdown.Toggle>

                            <Dropdown.Content>
                              {usePermission('Editar ementas', 'Ementas') && <Dropdown.Item onClick={() => editTaxonomyForm('taxonomies', taxonomy)}>{t('button.edit')}</Dropdown.Item>}
                              {usePermission('Excluir ementas', 'Ementas') && <Dropdown.Item onClick={() => destroyTaxonomyForm('taxonomies', taxonomy)}>{t('button.delete')}</Dropdown.Item>}
                            </Dropdown.Content>
                          </Dropdown>
                        ) : null}
                      </div>
                    ))}
                </div>
              </div>

              <div className="taxonomies__column">
                {usePermission('Criar ementas', 'Ementas') && taxonomies.some(t => t.isActive) && (
                  <button
                    className="btn btn--outline btn--full"
                    onClick={() => newTaxonomyForm('topics')}
                  >
                    {t('taxonomyAndMaterials.buttonAddTaxonomy')}
                  </button>
                )}

                <div className="taxonomies__list">
                  {topics &&
                    topics.map((taxonomy, index) => (
                      <div
                        key={index}
                        className={`taxonomies__item ${taxonomy.isActive ? 'active' : ''}`}
                        onClick={() => selectTaxonomy(taxonomy, 'topics', 'subTopics')}
                      >
                        <span className="taxonomies__text">{taxonomy.name}</span>

                        <Dropdown>
                          <Dropdown.Toggle>{usePermission('Editar ementas', 'Ementas') && <FiMoreHorizontal />}</Dropdown.Toggle>

                          <Dropdown.Content>
                            <Dropdown.Item onClick={() => editTaxonomyForm('taxonomies', taxonomy)}>{t('button.edit')}</Dropdown.Item>

                            <Dropdown.Item onClick={() => destroyTaxonomyForm('topics', taxonomy)}>Deletar</Dropdown.Item>
                          </Dropdown.Content>
                        </Dropdown>
                      </div>
                    ))}

                  {!loading && topics.length === 0 && (
                    <EmptyState
                      type="select"
                      text={t('emptyState.selectTopicText')}
                    />
                  )}
                </div>
              </div>

              <div className="taxonomies__column">
                {topics.some(t => t.isActive) && (
                  <button
                    className="btn btn--outline btn--full"
                    onClick={() => newTaxonomyForm('subTopics')}
                  >
                    {t('taxonomyAndMaterials.buttonAddTaxonomy')}
                  </button>
                )}

                <div className="taxonomies__list">
                  {subTopics &&
                    subTopics.map((taxonomy, index) => (
                      <div
                        key={index}
                        className="taxonomies__item"
                      >
                        <span className="taxonomies__text">{taxonomy.name}</span>

                        <Dropdown>
                          <Dropdown.Toggle>
                            <FiMoreHorizontal />
                          </Dropdown.Toggle>

                          <Dropdown.Content>
                            <Dropdown.Item onClick={() => editTaxonomyForm('taxonomies', taxonomy)}>{t('button.edit')}</Dropdown.Item>
                            <Dropdown.Item onClick={() => destroyTaxonomyForm('subTopics', taxonomy)}>Deletar</Dropdown.Item>
                          </Dropdown.Content>
                        </Dropdown>
                      </div>
                    ))}

                  {!loading && subTopics.length === 0 && (
                    <EmptyState
                      type="select"
                      text={t('emptyState.selectSubtopicText')}
                    />
                  )}
                </div>
              </div>

              <Modal
                show={showModal}
                onClose={() => {
                  setShowModal(false);
                  setNewTaxonomyName('');
                }}
              >
                <form
                  method="post"
                  onSubmit={addNewTaxonomy}
                  className="form"
                >
                  <h2 className="form__title">{t('taxonomyAndMaterials.createTaxonomy')}</h2>

                  <div className="form__row">
                    <label
                      className="form__label"
                      htmlFor="classCourse"
                    >
                      {t('textsCommon.name')}
                    </label>
                    <input
                      className="form__control"
                      id="name"
                      name="name"
                      type="text"
                      placeholder={t('taxonomyAndMaterials.placeholderTaxonomyName')}
                      value={newTaxonomyName}
                      onChange={e => setNewTaxonomyName(e.target.value)}
                    />
                  </div>

                  <button
                    className="btn btn--primary"
                    type="submit"
                    disabled={addingTaxonomy || !newTaxonomyName}
                  >
                    {t('taxonomyAndMaterials.buttonCreateNewTaxonomy')}
                  </button>
                </form>
              </Modal>

              <Modal
                show={editModal}
                onClose={() => setEditModal(false)}
              >
                <form
                  method="patch"
                  onSubmit={editCurrentTaxonomy}
                  className="form"
                >
                  <h2 className="form__title">{t('taxonomyAndMaterials.buttonEditTaxonomy')}</h2>

                  <div className="form__row">
                    <label
                      className="form__label"
                      htmlFor="classCourse"
                    >
                      {t('textsCommon.name')}
                    </label>
                    <input
                      className="form__control"
                      id="name"
                      name="name"
                      type="text"
                      placeholder={t('taxonomyAndMaterials.placeholderNewTaxonomyName')}
                      value={editTaxonomyName}
                      onChange={e => setEditTaxonomyName(e.target.value)}
                    />
                  </div>

                  <button
                    className="btn btn--primary"
                    type="submit"
                    disabled={addingTaxonomy}
                  >
                    {t('taxonomyAndMaterials.buttonEditTaxonomy')}
                  </button>
                </form>
              </Modal>
            </div>
          </div>
        </>
      ) : (
        <Error404 />
      )}
    </>
  );
};
